import { flow, getParent, getRoot, Instance, types } from 'mobx-state-tree';
import { CoreApi, SDKapi } from 'service/axios-api';
import { IRootStore } from '../root';
import { IProductLibrary } from './product-library';

export interface IMaintenanceStore extends Instance<typeof MaintenanceStore> {}

const { application } = window;

const MAPLE_STORY_PRODUCT_ID = '10100';
const TOY_LIST = [MAPLE_STORY_PRODUCT_ID];

const TOY_GID_MAP: Record<string, Record<string, string>> = {
    [MAPLE_STORY_PRODUCT_ID]: {
        live: '2570',
        pre: '2574',
        test: '2573',
    },
};

export const MaintenanceStore = types
    .model('MaintenanceStore', {
        mode: types.optional(types.string, ''),
        message: types.optional(types.string, ''),
    })
    .views((self) => ({
        get root(): IRootStore {
            return getRoot<IRootStore>(self);
        },
        get parent(): IProductLibrary {
            return getParent<IProductLibrary>(self);
        },
    }))
    .views((self) => ({
        // to avoid circular reference typescript error and better performance
        get productId(): string {
            return self.parent.productId;
        },
        get useToy(): boolean {
            return self.root.systemInfo.onMac && TOY_LIST.includes(self.parent.productId);
        },
    }))
    .actions((self) => ({
        set(mode: string, message: string) {
            self.mode = mode;
            self.message = message.trim();
        },
        clear() {
            self.mode = '';
        },
    }))
    .actions((self) => ({
        fetchToy: flow(function* () {
            try {
                const gid = TOY_GID_MAP[self.productId][application.params.environment];
                if (gid) {
                    const lang = self.root.systemInfo.locale.replace('_', '-').split('-')[0];
                    const { data } = yield SDKapi.get(`/access-control/execution-controls/now`, {
                        headers: {
                            'client-type': 'arena-launcher',
                            gid,
                            locale: `${lang}_US`, // 국가는 미국 고정
                        },
                    });

                    if (data.code === 0 && Array.isArray(data.result) && data.result.length === 1) {
                        const result = data.result[0];
                        if (result.isMaintenance && result.maintenance.type === 'MAINTENANCE') {
                            self.set('normal', result.maintenance.exposureMessage.message);
                            return;
                        }
                    }
                    self.clear();
                } else {
                    self.clear();
                }
            } catch (e) {
                self.clear();
            }
        }),
    }))
    .actions((self) => ({
        fetch: flow(function* (onlyArena = false) {
            try {
                if (self.useToy && !onlyArena) {
                    yield self.fetchToy();

                    if (self.mode === 'normal') {
                        return;
                    }
                }

                const { data } = yield CoreApi.get(`/maintenance/v1/products/${self.productId}`, {
                    params: { lang: self.root.systemInfo.lang },
                });
                self.set(data.maintenanceMode, data.message);
            } catch (e) {
                // if there is no maintenance, 404 return
                self.clear();
            }
        }),
    }));
